import { useState } from "react";
import MetricCard from "./components/MetricCard";
import DateRangeControl from "./components/DateRangeControl";
import PremiumCallout from "./components/PremiumCallout";
import UsageChart from "./components/UsageChart";
import EventTable from "./components/EventTable";
import { ChartSkeleton } from "./components/LoadingSkeleton";
import EmptyState from "./components/EmptyState";
import { formatNumber, formatRatio, sumSources } from "./utils";

// Ordered so "wider" always means "the next step up" — today < 24h < 7d < 30d < 90d.
// 'custom' has no defined "wider" step; it's whatever range the person picked.
const WIDEN_CHAIN = ["today", "24h", "7d", "30d", "90d"];
const PRESET_LABELS = {
  today: "today",
  "24h": "the last 24 hours",
  "7d": "the last 7 days",
  "30d": "the last 30 days",
  "90d": "the last 90 days",
};

const nextWiderPreset = (preset) => {
  const idx = WIDEN_CHAIN.indexOf(preset);
  if (idx === -1 || idx === WIDEN_CHAIN.length - 1) return null; // custom, or already at the widest step
  return WIDEN_CHAIN[idx + 1];
};

/**
 * EventAnalytics — data-independent module. All state (date range, selection,
 * sort, pagination, loading/error) is owned by the parent (EventsStatsPage) and
 * passed in as props; this component only renders and emits change callbacks.
 *
 * See README.md for the full prop contract.
 */
const EventAnalytics = ({
  dateRange,
  onDateRangeChange,
  resolution,
  resolutionLocked = false,
  chartSeries = [],
  selectedEvent = null,
  onSelectEvent,
  focusView: controlledFocusView,
  onFocusViewChange,
  events = { items: [], pagination: { page: 1, limit: 10, total: 0, total_pages: 1 } },
  sort,
  onSortChange,
  page,
  onPageChange,
  loading = { chart: false, table: false },
  error = { chart: null, table: null },
  billingPath = "/billing",
}) => {
  // focusView can be local-only if the parent doesn't want to control it
  const [localFocusView, setLocalFocusView] = useState(false);
  const focusView = controlledFocusView ?? localFocusView;
  const setFocusView = onFocusViewChange ?? setLocalFocusView;

  const totals = chartSeries.reduce(
    (acc, bucket) => {
      const bySource = bucket.sources || {};
      acc.impressions += Number(bySource.widget) || 0;
      acc.views += sumSources({ ...bySource, widget: 0 });
      return acc;
    },
    { impressions: 0, views: 0 }
  );
  const overallRatio = totals.impressions > 0 ? Math.round((totals.views / totals.impressions) * 100) : null;
  const hasActivity = totals.impressions + totals.views > 0;

  // True only once both fetches have settled with no errors and genuinely
  // nothing happened in range — this is what decides whether we render the
  // chart+table at all, or a single consolidated empty state instead of two
  // separate (and visually inconsistent) empty states stacked on the page.
  const isEmptyOverall =
    !loading.chart && !loading.table && !error.chart && !error.table && !hasActivity;

  const widerPreset = nextWiderPreset(dateRange.preset);

  const handleWidenRange = () => {
    if (widerPreset) {
      onDateRangeChange({ preset: widerPreset });
    }
  };

  return (
    <div className="ea-root">
      {/* No in-component title — this module is rendered inside a card whose
          heading ("Reports: Event Engagement") comes from the page template. */}
      <div className="ea-header">
        <DateRangeControl
          preset={dateRange.preset}
          onChange={onDateRangeChange}
          onOpenCustomRange={() => onDateRangeChange({ preset: "custom" })}
        />
      </div>

      <div className="ea-metrics">
        <MetricCard label="Impressions" value={totals.impressions} icon="visibility" />
        <MetricCard label="Views" value={totals.views} icon="ads_click" />
        <MetricCard
          label="Overall ratio"
          value={formatRatio(overallRatio)}
          isRaw={false}
          icon="percent"
        />
        <MetricCard label="Active events" value={events.pagination.total} icon="event" />
      </div>

      {isEmptyOverall ? (
        <EmptyState
          title="No activity in this range"
          description={
            widerPreset
              ? "Try a wider date range, or check back once events start collecting views."
              : `No engagement recorded in ${PRESET_LABELS[dateRange.preset] || "the selected period"}. Check back once events start collecting views.`
          }
          onAction={widerPreset ? handleWidenRange : undefined}
          actionLabel={widerPreset ? `Try ${PRESET_LABELS[widerPreset]}` : undefined}
        />
      ) : (
        <>
          <div className="ea-chart-section">
            {selectedEvent && (
              <div className="ea-chart-section__toolbar">
                <span className="ea-chart-section__selected-chip">
                  {selectedEvent.name}
                  <button type="button" onClick={() => onSelectEvent(null)} aria-label="Clear selection">
                    <i className="material-icons">close</i>
                  </button>
                </span>
                <button
                  type="button"
                  className={`btn btn-sm ${focusView ? "btn-secondary" : "btn-outline-secondary"}`}
                  onClick={() => setFocusView(!focusView)}
                >
                  Focus view
                </button>
              </div>
            )}

            {resolutionLocked && <PremiumCallout billingPath={billingPath} />}

            {loading.chart ? (
              <ChartSkeleton />
            ) : error.chart ? (
              <EmptyState title="Couldn't load chart" description={error.chart} />
            ) : (
              <UsageChart
                series={chartSeries}
                selectedEvent={selectedEvent}
                resolution={resolution}
                focusView={focusView}
              />
            )}
          </div>

          <div className="ea-table-section">
            <p className="ea-table-section__label">Events</p>
            <EventTable
              events={events.items}
              pagination={events.pagination}
              sort={sort}
              onSortChange={onSortChange}
              page={page}
              onPageChange={onPageChange}
              selectedEventUuid={selectedEvent?.event_uuid}
              onSelectEvent={onSelectEvent}
              loading={loading.table}
              error={error.table}
              onWidenRange={widerPreset ? handleWidenRange : undefined}
              widenLabel={widerPreset ? `Try ${PRESET_LABELS[widerPreset]}` : undefined}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EventAnalytics;
